/**
 * Chat Message Limit Service
 * Manages daily message limits for free tier users
 */

import firebase from "firebase/compat/app";
import { db } from "../firebase";
import { getUserBillingState } from "../../../database/firestore";
import { isTrialActive } from "./billingService";
import { isEartagUser } from "../../../config/billingConfig";

/**
 * Free tier daily message limit
 */
const FREE_TIER_DAILY_LIMIT = 20;

/**
 * Gets the current date in YYYY-MM-DD format (UTC)
 * @returns {string} Date string
 */
function getCurrentDateString() {
	const now = new Date();
	const year = now.getUTCFullYear();
	const month = String(now.getUTCMonth() + 1).padStart(2, "0");
	const day = String(now.getUTCDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
}

/**
 * Checks if user is an eartag user (has physical eartags, no billing required)
 * @param {string} userId - The unique identifier for the user
 * @returns {Promise<boolean>} True if user is an eartag user
 */
async function isEartagUserById(userId) {
	try {
		const userDoc = await db.collection("Users").doc(userId).get();
		if (!userDoc.exists) {
			return false; // New users default to SaaS (noEartags: true)
		}
		const userData = userDoc.data();
		return isEartagUser(userData);
	} catch (error) {
		console.error("[chatMessageLimitService][isEartagUserById] - Error:", error);
		return false; // Default to SaaS on error
	}
}

/**
 * Gets the daily message limit for a user based on their billing tier
 * @param {string} userId - The unique identifier for the user
 * @returns {Promise<number|null>} Daily limit (null = unlimited)
 */
export async function getDailyLimit(userId) {
	try {
		// Check if user is eartag user - unlimited access
		const isEartag = await isEartagUserById(userId);
		if (isEartag) {
			return null; // Unlimited
		}

		// Check if user is in trial - unlimited access
		const inTrial = await isTrialActive(userId);
		if (inTrial) {
			return null; // Unlimited
		}

		// Get billing state
		const billingState = await getUserBillingState(userId);
		const packageTier = billingState?.PackageTier || "free";

		// Free tier has limit, all others are unlimited
		if (packageTier === "free") {
			return FREE_TIER_DAILY_LIMIT;
		}

		return null; // Unlimited for paid tiers
	} catch (error) {
		console.error("[chatMessageLimitService][getDailyLimit] - Error:", error);
		// Fail open - default to unlimited on error
		return null;
	}
}

/**
 * Gets the current day's message count for a user
 * @param {string} userId - The unique identifier for the user
 * @returns {Promise<number>} Current message count for today
 */
export async function getMessageCount(userId) {
	try {
		const dateString = getCurrentDateString();
		const limitDoc = await db
			.collection("Users")
			.doc(userId)
			.collection("ChatLimits")
			.doc(dateString)
			.get();

		if (!limitDoc.exists) {
			return 0;
		}

		const data = limitDoc.data();
		return data?.messageCount || 0;
	} catch (error) {
		console.error("[chatMessageLimitService][getMessageCount] - Error:", error);
		// Fail open - return 0 on error
		return 0;
	}
}

/**
 * Increments the daily message count for a user
 * @param {string} userId - The unique identifier for the user
 * @returns {Promise<number>} Updated message count
 */
export async function incrementMessageCount(userId) {
	try {
		const dateString = getCurrentDateString();
		const limitRef = db
			.collection("Users")
			.doc(userId)
			.collection("ChatLimits")
			.doc(dateString);

		const limitDoc = await limitRef.get();

		if (limitDoc.exists) {
			// Increment existing count
			await limitRef.update({
				messageCount: firebase.firestore.FieldValue.increment(1),
				lastUpdated: firebase.firestore.FieldValue.serverTimestamp(),
			});

			const updatedDoc = await limitRef.get();
			return updatedDoc.data()?.messageCount || 0;
		} else {
			// Create new document for today
			await limitRef.set({
				messageCount: 1,
				date: dateString,
				lastUpdated: firebase.firestore.FieldValue.serverTimestamp(),
			});

			return 1;
		}
	} catch (error) {
		console.error("[chatMessageLimitService][incrementMessageCount] - Error:", error);
		// Fail open - return 0 on error (don't block message)
		return 0;
	}
}

/**
 * Checks if user can send a message based on their billing tier and daily limit
 * @param {string} userId - The unique identifier for the user
 * @returns {Promise<{canSend: boolean, remaining?: number, limit?: number, reason?: string}>}
 */
export async function canSendMessage(userId) {
	try {
		// Check if user is eartag user - always allow
		const isEartag = await isEartagUserById(userId);
		if (isEartag) {
			return { canSend: true, reason: "eartag_user" };
		}

		// Check if user is in trial - always allow
		const inTrial = await isTrialActive(userId);
		if (inTrial) {
			return { canSend: true, reason: "trial_active" };
		}

		// Get billing state
		const billingState = await getUserBillingState(userId);
		const packageTier = billingState?.PackageTier || "free";

		// Paid tiers have unlimited access
		if (packageTier !== "free") {
			return { canSend: true, reason: "paid_tier" };
		}

		// Free tier - check limit
		const limit = await getDailyLimit(userId);
		if (limit === null) {
			// Shouldn't happen for free tier, but fail open
			return { canSend: true, reason: "unlimited" };
		}

		const currentCount = await getMessageCount(userId);
		const remaining = Math.max(0, limit - currentCount);

		if (currentCount >= limit) {
			return {
				canSend: false,
				remaining: 0,
				limit,
				reason: "limit_exceeded",
			};
		}

		return {
			canSend: true,
			remaining,
			limit,
			reason: "free_tier",
		};
	} catch (error) {
		console.error("[chatMessageLimitService][canSendMessage] - Error:", error);
		// Fail open - allow message on error
		return { canSend: true, reason: "error_fallback" };
	}
}

